<script lang="ts" setup>
import { computed } from 'vue';
import { AppTypography } from '~/components/base';
import type { Campaign } from '~/types/Candidates';
import { opensCampaignModal } from '~/utils/campaignLink';
import { $stripHtml } from '~/utils/helpers';

const { campaign } = defineProps<{
  campaign: Campaign;
}>();

const emit = defineEmits<{ open: [campaign: Campaign] }>();

const plainDescription = computed(() => $stripHtml(campaign.description));

const href = computed(() => `/campaigns/${campaign.id}`);

// A plain <a> rather than NuxtLink: NuxtLink attaches its own click handler and
// navigates before a `@click` listener on the component can preventDefault, so
// the modal never got a chance. The href is real, so crawlers and modified
// clicks behave as normal links.
function onClick(event: MouseEvent) {
  if (!opensCampaignModal(event)) return;

  event.preventDefault();
  emit('open', campaign);
}
</script>

<template>
  <a
    :href="href"
    data-testid="campaign-card"
    class="campaign-card flex flex-col no-underline"
    @click="onClick"
  >
    <div
      class="campaign-card__body flex flex-1 items-center gap-3 lg:flex-col lg:items-start"
    >
      <div class="flex min-w-0 flex-1 flex-col">
        <!-- No type pill here: the Campaigns listing shows title, description
             and Learn More only (Figma). The pill stays on CampaignMiniCard,
             which the party and candidate profile Campaigns tabs use. -->
        <AppTypography
          type="body-bold"
          element="p"
          class="campaign-card__title mb-2"
        >
          {{ campaign.title }}
        </AppTypography>

        <AppTypography
          type="subheadline"
          element="p"
          class="campaign-card__description text-color-tag"
        >
          {{ plainDescription }}
        </AppTypography>

        <span
          data-testid="campaign-learn-more"
          class="campaign-card__learn-more mt-4 hidden w-fit items-center gap-1.5 lg:mt-auto lg:flex"
        >
          <AppTypography type="footnote-bold" class="underline"
            >Learn More</AppTypography
          >
          <Icon name="solar:arrow-right-linear" size="20" class="shrink-0" />
        </span>
      </div>

      <span class="campaign-card__chevron shrink-0 lg:hidden">
        <Icon name="solar:alt-arrow-right-linear" class="text-color-subtext" />
      </span>
    </div>
  </a>
</template>

<style scoped lang="scss">
.campaign-card {
  border: 1px solid $border-color;
  border-radius: $radius-lg;
  background-color: $surface-color-bg;
  color: inherit;

  // Desktop: every card in the grid is the same height regardless of how
  // many lines its title/description wrap to — title and description are
  // both line-clamped (below) so this height is never too short to fit
  // them. Mobile keeps its natural (row-list) height.
  @media (min-width: 1024px) {
    height: 220px;
    overflow: hidden;
  }

  &__body {
    padding: $spacing-lg;
  }

  &__title {
    @media (min-width: 1024px) {
      display: -webkit-box;
      overflow: hidden;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }
  }

  &__description {
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }

  &__learn-more {
    border: none;
    background: transparent;
    color: $text-color-brand;
    cursor: pointer;
  }
}
</style>
